import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import AdminHeader from '../../../components/ui/AdminHeader';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { 
  getAllEvents, 
  updateEventStatus, 
  updateEventFeatured, 
  deleteEvent,
  getRecentEvents,
  getTopVenuesBySales,
  getTopMerchandiseAccounts,
  searchEventsAdvanced
} from '../../../services/adminService';

const AdminEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [topVenues, setTopVenues] = useState([]);
  const [topMerchandise, setTopMerchandise] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Check if user is admin
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [userProfile, navigate]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedStatus) filters.status = selectedStatus;
      
      const data = await getAllEvents(filters);
      setEvents(data || []);
    } catch (error) {
      setError(`Failed to load events: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      setAnalyticsLoading(true);
      const [recentEventsData, topVenuesData, topMerchandiseData] = await Promise.all([
        getRecentEvents(10),
        getTopVenuesBySales(10),
        getTopMerchandiseAccounts(10)
      ]);

      setRecentEvents(recentEventsData || []);
      setTopVenues(topVenuesData || []);
      setTopMerchandise(topMerchandiseData || []);
    } catch (error) {
      setError(`Failed to load analytics: ${error?.message}`);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query?.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      const results = await searchEventsAdvanced(query);
      setSearchResults(results || []);
    } catch (error) {
      setError(`Search failed: ${error?.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    loadEvents();
  }, [selectedStatus]);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      setError('');
      await updateEventStatus(eventId, newStatus);
      setSuccess('Event status updated successfully');
      loadEvents();
    } catch (error) {
      setError(`Failed to update status: ${error?.message}`);
    }
  };

  const handleFeaturedToggle = async (eventId, currentFeatured) => {
    try {
      setError('');
      await updateEventFeatured(eventId, !currentFeatured);
      setSuccess(`Event ${!currentFeatured ? 'featured' : 'unfeatured'} successfully`);
      loadEvents();
    } catch (error) {
      setError(`Failed to update featured status: ${error?.message}`);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }
    
    try {
      setError('');
      await deleteEvent(eventId);
      setSuccess('Event deleted successfully');
      loadEvents();
    } catch (error) {
      setError(`Failed to delete event: ${error?.message}`);
    }
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const eventStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const displayEvents = useMemo(() => {
    return searchTerm?.trim() ? searchResults : events;
  }, [searchTerm, searchResults, events]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    })?.format(amount);
  };

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader
        title="Event Oversight"
        description="Monitor and manage all platform events"
        user={{
          name: userProfile?.full_name || "Admin User",
          role: "Platform Administrator"
        }}
      />

      <main className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}

          {/* Enhanced Search Bar */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Search" size={20} color="white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Advanced Event Search</h2>
                <p className="text-sm text-muted-foreground">Search by event content, venue, date, or location</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  placeholder="Search events by title, content, venue, or date (e.g., 'today', 'this week', venue name)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  iconName="Search"
                />
                {isSearching && (
                  <p className="text-sm text-muted-foreground mt-2">Searching...</p>
                )}
                {searchTerm && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Found {searchResults?.length || 0} results for "{searchTerm}"
                  </p>
                )}
              </div>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e?.target?.value)}
                options={statusOptions}
              />
            </div>
          </div>

          {/* Analytics Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Events */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={16} color="#3b82f6" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Recent Events</h3>
              </div>
              
              {analyticsLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {recentEvents?.slice(0, 5)?.map((event) => (
                    <div key={event?.id} className="border-l-4 border-primary pl-3">
                      <div className="font-medium text-sm text-foreground line-clamp-1">
                        {event?.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {event?.venue?.name} • {new Date(event?.created_at)?.toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  {recentEvents?.length === 0 && (
                    <div className="text-sm text-muted-foreground">No recent events</div>
                  )}
                </div>
              )}
            </div>

            {/* Top Venues */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={16} color="#10b981" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Top Venues by Sales</h3>
              </div>
              
              {analyticsLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {topVenues?.slice(0, 5)?.map((venue, index) => (
                    <div key={venue?.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-medium text-sm text-foreground line-clamp-1">
                            {venue?.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {venue?.city} • {venue?.event_count} events
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-green-600">
                        {formatCurrency(venue?.total_sales)}
                      </div>
                    </div>
                  ))}
                  {topVenues?.length === 0 && (
                    <div className="text-sm text-muted-foreground">No sales data available</div>
                  )}
                </div>
              )}
            </div>

            {/* Top Merchandise */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="Package" size={16} color="#8b5cf6" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Top Merchandise Accounts</h3>
              </div>
              
              {analyticsLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {topMerchandise?.slice(0, 5)?.map((account, index) => (
                    <div key={account?.organizer_id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-medium text-sm text-foreground line-clamp-1">
                            {account?.organizer_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {account?.total_merchandise_items} items • {account?.events_count} events
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-purple-600">
                        {formatCurrency(account?.total_potential_value)}
                      </div>
                    </div>
                  ))}
                  {topMerchandise?.length === 0 && (
                    <div className="text-sm text-muted-foreground">No merchandise data available</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Events Table */}
          <div className="bg-card border border-border rounded-lg">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Calendar" size={20} color="white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Event Management</h2>
                    <p className="text-sm text-muted-foreground">
                      {searchTerm ? `Search Results (${displayEvents?.length})` : `All Events (${displayEvents?.length})`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Event</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Organizer</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Venue</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Featured</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading || isSearching ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-muted-foreground">
                        {loading ? 'Loading events...' : 'Searching...'}
                      </td>
                    </tr>
                  ) : displayEvents?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-muted-foreground">
                        {searchTerm ? 'No events found matching your search' : 'No events found'}
                      </td>
                    </tr>
                  ) : (
                    displayEvents?.map((event) => (
                      <tr key={event?.id} className="border-t border-border hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-foreground">{event?.title}</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              {event?.short_description || event?.description}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="font-medium text-foreground">{event?.organizer?.full_name}</div>
                            <div className="text-muted-foreground">{event?.organizer?.email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="font-medium text-foreground">{event?.venue?.name}</div>
                            <div className="text-muted-foreground">{event?.venue?.city}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(event?.event_date)?.toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={event?.status}
                            onChange={(e) => handleStatusChange(event?.id, e?.target?.value)}
                            options={eventStatusOptions}
                            size="sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant={event?.is_featured ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleFeaturedToggle(event?.id, event?.is_featured)}
                          >
                            {event?.is_featured ? 'Featured' : 'Feature'}
                          </Button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/event-detail/${event?.id}`)}
                            >
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteEvent(event?.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEventsPage;